import React from "react";
import { Action, Dispatch } from "redux";
import { TableDataRowProps, TableHeaderProps, TableProps } from "../../../Common/UI/Index";
import { IScheduleInstanceType } from "../CreateSchedule/redux/Types";
import { CreateScheduleSlice } from "../CreateSchedule/redux";
import { ReccurrenceEditItem } from "../EditReccurrenceForm/Types";
import { getFormattedDate } from "../Helper/Script";
import { ACTIVE, INACTIVE } from "../Helper/Constants";
import Edit from "~icons/ic/outline-edit";
import "./Style.scss";

/**
 * To render the action column icons
 * @returns the icons
 * NOTE: Type any is used because UI Table accepts only strings as values
 * but we need icons in the table row
 */
const getActionColumn = (item: IScheduleInstanceType, dispatch: Dispatch<Action>, idx: number): any => {

  /**
   * Handle edit action
   */
  const onHandleEdit = (): void => {
    const recurrenceItem: ReccurrenceEditItem = {
      extractScheduleInstanceId: item.id,
      surveyName: item?.outputConfigs?.[0]?.surveyName || "",
      runDate: item.runDate && getFormattedDate(
        new Date(item.runDate), false, true
      ),
      relativeRunDate: item?.executionDateParameters?.relativeRunDate && getFormattedDate(
        new Date(item?.executionDateParameters?.relativeRunDate), false, true
      ),
      reportingPeriodStartDate: item?.executionDateParameters?.rpsd && getFormattedDate(
        new Date(item?.executionDateParameters?.rpsd), false, true
      ),
      reportingPeriodEndDate: item?.executionDateParameters?.rped && getFormattedDate(
        new Date(item?.executionDateParameters?.rped), false, true
      ),
      outputConfigId: item?.outputConfigs?.[0]?.id,
      outputConfigName: item?.outputConfigs?.[0]?.name,
      isActive: item.isActive
    } as ReccurrenceEditItem;
    dispatch(CreateScheduleSlice.actions.setSchedulingRecurrenceItem(recurrenceItem));
    dispatch(CreateScheduleSlice.actions.setShowSchedulingPopup(true));
    dispatch(CreateScheduleSlice.actions.setSchedulingId(idx + 1));
  };
  return (
    <div>
      <Edit
        title="edit"
        onClick={onHandleEdit}
      />
    </div>
  );
};

/**
* Method to handle styling with respect to active and inactive of the schedule instance
* @param statusType Schedule instance status
* @returns JSX element
*/
const getStatus = (statusType: boolean): JSX.Element => {
  const formattedStatus = statusType ? ACTIVE : INACTIVE;
  return <div className={`ebt-schedule-isactive__${formattedStatus}`}>{formattedStatus}</div>;
};

/**
 * To bind the table data
 * @param data response data
 * @returns the formatted table row data
 */
const buildDataRow = (data: IScheduleInstanceType[], dispatch: Dispatch<Action>): TableDataRowProps[] => {
  let outputData: TableDataRowProps[] = [];
  const inputData = data;
  outputData = inputData.map((item: IScheduleInstanceType, idx: number) => {
    const output: TableDataRowProps = {
      row: [
        {
          value: (idx + 1),
          propertyName: "id"
        },
        {
          value: item?.outputConfigs[0]?.name || "-",
          propertyName: "name"
        },
        {
          value: item?.outputConfigs[0]?.surveyName || "-",
          propertyName: "outputConfig"
        },
        {
          value: item.runDate ? getFormattedDate(
            new Date(item.runDate), true) : "-",
          propertyName: "runDate"
        },
        {
          value: item?.executionDateParameters?.relativeRunDate ? getFormattedDate(
            new Date(item?.executionDateParameters?.relativeRunDate), true) : "-",
          propertyName: "relativeRunDate"
        },
        {
          value: item?.executionDateParameters?.rpsd ? getFormattedDate(
            new Date(item?.executionDateParameters?.rpsd), true) : "-",
          propertyName: "reportingPeriodStartDate"
        },
        {
          value: item?.executionDateParameters?.rped ? getFormattedDate(
            new Date(item?.executionDateParameters?.rped), true) : "-",
          propertyName: "reportingPeriodEndDate"
        },
        {
          value: getStatus(item.isActive) || "-",
          propertyName: "status"
        },
        {
          value: getActionColumn(item, dispatch, idx),
          propertyName: "actions"
        }
      ]
    };
    return output;
  });
  return outputData;
};

/**
 * To populate the table data
 * @param data response data
 * @returns the formatted data to bind the table
 */
export const PopulateTable = (data: IScheduleInstanceType[], dispatch: Dispatch<Action>): TableProps => {
  let table: TableProps = {} as TableProps;
  const tableHeader: TableHeaderProps[] = [
    {
      title: "Scheduling.ScheduleInstanceTable.ScheduleInstanceId",
      propertyName: "id",
      width: "4%"
    },
    {
      title: "Scheduling.ScheduleInstanceTable.Name",
      propertyName: "name",
      width: "12%"
    },
    {
      title: "Scheduling.ScheduleInstanceTable.OutputConfig",
      propertyName: "outputConfig",
      width: "13%"
    },
    {
      title: "Scheduling.ScheduleInstanceTable.RunDate",
      propertyName: "runDate",
      width: "14%"
    },
    {
      title: "Scheduling.ScheduleInstanceTable.RelativeRunDate",
      propertyName: "relativeRunDate",
      width: "14%"
    },
    {
      title: "Scheduling.ScheduleInstanceTable.ReportingPeriodStartDate",
      propertyName: "reportingPeriodStartDate",
      width: "14%"
    },
    {
      title: "Scheduling.ScheduleInstanceTable.ReportingPeriodEndDate",
      propertyName: "reportingPeriodEndDate",
      width: "14%"
    },
    {
      title: "Scheduling.ScheduleInstanceTable.Status",
      propertyName: "status",
      width: "9%"
    },
    {
      title: "Scheduling.ScheduleInstanceTable.Actions",
      propertyName: "actions",
      width: "6%"
    }
  ];
  table = {
    headerRow: tableHeader,
    dataRow: buildDataRow(data, dispatch)
  };
  return table;
};
